<template>
	<div
		class="relative h-60 w-full overflow-hidden"
		:class="{ 'bg-black': !noBg }"
	>
		<Transition
			enterActiveClass="transition-opacity duration-1000"
			leaveActiveClass="transition-opacity duration-1000"
			enterFromClass="opacity-0"
			leaveToClass="opacity-0"
		>
			<RouletteRipple
				:horizontal="props.horizontal"
				:color="animationColor"
				:animate="pullBackAnimate"
				:prefix="`item${startOffset || 0}`"
				v-if="pullBackRipple && !noBg"
			/>
		</Transition>

		<div
			class="absolute bottom-0 flex h-full items-center"
			:class="{
				'duration-3000':
					!fast && !drift && !pullBack && !startOffset && transition,
				'duration-3300':
					!fast && !drift && !pullBack && startOffset === 1 && transition,
				'duration-3600':
					!fast && !drift && !pullBack && startOffset === 2 && transition,
				'duration-3900':
					!fast && !drift && !pullBack && startOffset === 3 && transition,
				'duration-1000':
					fast && !drift && !pullBack && !startOffset && transition,
				'duration-1100':
					fast && !drift && !pullBack && startOffset === 1 && transition,
				'duration-1200':
					fast && !drift && !pullBack && startOffset === 2 && transition,
				'duration-1300':
					fast && !drift && !pullBack && startOffset === 3 && transition,
				'ease-out': !drift && !pullBack && transition,
				'duration-500 ease-[cubic-bezier(0.4,_0.0,_0.3,_2)]':
					pullBack && transition,
				'animate-drift-y': !horizontal && drift,
				'animate-drift-x': horizontal && drift,
				'top-2 right-0 left-0 flex-col gap-3': !horizontal,
				'top-0 left-1/2 w-fit gap-5': horizontal,
				'transition-transform': !drift && transition
			}"
			:style="{
				transform,
				'--one-item': `${oneItem}px`,
				'--item-count': COUNT
			}"
			ref="roulette"
		>
			<div
				v-for="(item, i) in rouletteItems"
				:key="i"
				:data-id="i"
				:class="{
					'h-16 w-16': !props.horizontal,
					'h-20 w-20 -translate-x-1/2': horizontal,
					'transition-all duration-500': pullBack,
					'z-1 scale-150': item.large,
					'*:animate-hover-1': drift && i % 5 === 0 && horizontal,
					'*:animate-hover-2': drift && i % 5 === 1 && horizontal,
					'*:animate-hover-3': drift && i % 5 === 2 && horizontal,
					'*:animate-hover-4': drift && i % 5 === 3 && horizontal,
					'*:animate-hover-5': drift && i % 5 === 4 && horizontal,
					'*:animate-swing-1': drift && i % 5 === 0 && !horizontal,
					'*:animate-swing-2': drift && i % 5 === 1 && !horizontal,
					'*:animate-swing-3': drift && i % 5 === 2 && !horizontal,
					'*:animate-swing-4': drift && i % 5 === 3 && !horizontal,
					'*:animate-swing-5': drift && i % 5 === 4 && !horizontal
				}"
				class="relative shrink-0"
				:style="{
					transform: `scale(${calcScale(i)})`
				}"
			>
				<LazySticker
					v-if="item.type === 'sticker'"
					:src="item.src"
					class="h-full w-full shrink-0 transition-opacity"
					:forceSize="240"
					:ref="(el) => (stickerRefs[i] = el)"
					:preloader="item.preloader || undefined"
				/>
				<LazyImage
					v-else
					:src="item.src"
					alt="spin"
					class="h-full w-full shrink-0 transition-opacity"
				>
					<StickerPreloader v-if="item.preloader" :preloader="item.preloader" />
				</LazyImage>
			</div>
		</div>

		<div
			class="absolute top-0 bottom-0 left-0 w-10 bg-gradient-to-r from-black/50 to-black/0"
			v-if="props.horizontal && !noBg"
		/>
		<div
			class="absolute top-0 right-0 bottom-0 w-10 bg-gradient-to-l from-black/50 to-black/0"
			v-if="props.horizontal && !noBg"
		/>
		<div
			class="absolute top-0 right-0 left-0 h-16 -translate-y-px transform bg-gradient-to-b from-black to-black/0"
			v-if="!props.horizontal && !noBg"
		/>
		<div
			class="absolute right-0 bottom-0 left-0 h-16 translate-y-px transform bg-gradient-to-t from-black to-black/0"
			v-if="!props.horizontal && !noBg"
		/>

		<RouletteExpensive v-if="animateExpensive" />
	</div>
</template>
<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import LazySticker from './LazySticker.vue'
import LazyImage from './LazyImage.vue'
import { sleep } from '@/utils/sleep'
import { WebApp } from '@grammyjs/web-app'
import StickerPreloader from './StickerPreloader.vue'
import RouletteRipple from './RouletteRipple.vue'
import RouletteExpensive from './RouletteExpensive.vue'
import { playHapticSequence } from '@/utils/haptic'
import { useSeededRandom } from '@/composables/useSeededRandom'
import { getItemColor } from '@/utils/itemColor'

const COUNT = 60

const oneItem = computed(() => {
	return props.horizontal ? 100 : 76
})

const calcScale = (index: number) => {
	const abs =
		Math.abs(currentDrift.value) +
		oneItem.value * 0.5 +
		(!props.horizontal ? oneItem.value : 0)
	const center = oneItem.value * index + oneItem.value * 0.5
	const distance = Math.abs(abs - center)

	if (distance > oneItem.value) {
		return 1
	}

	return 1 + (0.2 * (oneItem.value - distance)) / oneItem.value
}

const props = defineProps<{
	startOffset?: number
	items: [string, string | null | undefined, number][]
	casePrice: number
	horizontal?: boolean
	noDrift?: boolean
	seed?: string
	noBg?: boolean
}>()

const emit = defineEmits<{
	(e: 'ready'): void
}>()

const rouletteItems = ref<
	{
		type: 'sticker' | 'image'
		src: string
		large: boolean
		reveal: boolean
		expensive: boolean
		preloader?: string | null
		chance: number
	}[]
>([])
const stickerRefs = ref<any[]>([])

const fillItems = () => {
	if (!props.items.length) {
		return
	}

	const seed = props.seed || Math.random().toString()
	const fullSeed = `${seed}:${props.startOffset || 0}`
	const seededRandom = useSeededRandom(fullSeed)

	const newArray = []
	stickerRefs.value = []

	for (let i = 0; i < COUNT; i++) {
		const real = seededRandom.next()
		const item = props.items[Math.floor(real * props.items.length)]

		newArray.push({
			type: (item[0].includes('.tgs') ? 'sticker' : 'image') as
				| 'sticker'
				| 'image',
			src: item[0],
			preloader: item[1],
			expensive: props.casePrice ? item[2] / props.casePrice > 5 : false,
			large: false,
			reveal: false,
			chance: 100
		})
	}

	rouletteItems.value = newArray
}

const setItem = (
	index: number,
	item: {
		src: string
		preloader?: string | null
		chance: number
		expensive: boolean
	}
) => {
	if (index < 0 || index >= COUNT) {
		return
	}

	rouletteItems.value[index].src = item.src
	rouletteItems.value[index].preloader = item.preloader
	rouletteItems.value[index].chance = item.chance
	rouletteItems.value[index].type = item.src.includes('.tgs')
		? 'sticker'
		: 'image'
	rouletteItems.value[index].expensive = item.expensive
}

watch(
	() => props.items,
	() => fillItems(),
	{ immediate: true }
)

let haptics = false
let lastPassedIndex: number | undefined = undefined

const startHaptics = () => {
	WebApp.HapticFeedback.notificationOccurred('success')

	haptics = true
}

const stopHaptics = () => {
	haptics = false
	lastPassedIndex = undefined
}

const currentIndex = ref(0)

const processHaptics = () => {
	if (!haptics) {
		return
	}

	currentIndex.value = Math.floor(Math.abs(currentDrift.value) / oneItem.value)

	if (lastPassedIndex !== currentIndex.value) {
		WebApp.HapticFeedback.selectionChanged()

		lastPassedIndex = currentIndex.value
	}
}

const animationFrame = ref(false)
const roulette = ref()

const currentDrift = ref(0)

const rotateCheck = () => {
	if (!animationFrame.value) {
		return
	}

	if (!roulette.value) {
		return
	}

	const transform = roulette.value.computedStyleMap().get('transform')

	if ((!transform[0]?.x || transform[0].x.unit !== 'px') && props.horizontal) {
		return
	}
	if ((!transform[0]?.y || transform[0].y.unit !== 'px') && !props.horizontal) {
		return
	}

	currentDrift.value = props.horizontal
		? transform[0].x.value
		: transform[0].y.value
}

const startAnimationFrame = () => {
	animationFrame.value = true

	rotateCheck()

	requestAnimationFrame(() => {
		const oldAngle = currentDrift.value
		rotateCheck()
		const newAngle = currentDrift.value
		if (oldAngle !== newAngle) {
			processHaptics()
		}

		if (animationFrame.value) {
			startAnimationFrame()
		}
	})
}

const stopAnimationFrame = () => {
	animationFrame.value = false
}

startAnimationFrame()

onUnmounted(() => stopAnimationFrame())

const transition = ref(false)
const drift = ref(false)
const transform = ref('')
const pullBack = ref(false)
const pullBackRipple = ref(false)
const pullBackAnimate = ref(false)
const animateExpensive = ref(false)
const animationColor = ref<'orange' | 'red' | 'pink' | 'blue'>('blue')
const fast = ref(false)

onMounted(async () => {
	if (props.noDrift) {
		return
	}

	await sleep(100)
	drift.value = true
})

const reset = async () => {
	animateExpensive.value = false
	pullBackAnimate.value = false
	pullBackRipple.value = false
	pullBack.value = false
	drift.value = false
	transition.value = false

	await nextTick()
	await sleep(1)

	const px = 5 * oneItem.value

	transform.value = props.horizontal
		? `translateX(${-px}px)`
		: `translateY(${-px}px)`

	fillItems()

	await nextTick()
	await sleep(10)

	transition.value = true
}

const randomExcludingRange = (excludeMin: number, excludeMax: number) => {
	const seed = props.seed || Math.random().toString()
	const fullSeed = `${seed}:${props.startOffset || 0}`
	const seededRandom = useSeededRandom(fullSeed)

	const allowedLength = excludeMin - 0 + (1 - excludeMax)
	const r = seededRandom.next() * allowedLength
	if (r < excludeMin) {
		return r
	} else {
		return excludeMax + (r - excludeMin)
	}
}

const roll = async (
	item: {
		src: string
		imgSrc: string
		preloader?: string | null
		price: number
		chance: number
	},
	fastSpin?: boolean,
	pullbackCallback?: () => void
) => {
	startAnimationFrame()
	fast.value = fastSpin || false

	await reset()

	await nextTick()

	const seed = props.seed || Math.random().toString()
	const fullSeed = `${seed}:${props.startOffset || 0}`
	const seededRandom = useSeededRandom(fullSeed)

	const itemNumber = 15 + Math.ceil(seededRandom.next() * (COUNT - 20))
	const randomOffset =
		Math.floor(randomExcludingRange(0.2, 0.7) * oneItem.value * 1.1) -
		oneItem.value * 0.5
	const px = (itemNumber - (!props.horizontal ? 1 : 0)) * oneItem.value

	if (item.price > props.casePrice) {
		const stickerItem = {
			src: item.src,
			preloader: item.preloader,
			price: item.price,
			chance: item.chance,
			expensive: props.casePrice ? item.price / props.casePrice > 5 : false
		}

		setItem(itemNumber, stickerItem)
	} else {
		const imgItem = {
			src: item.imgSrc,
			preloader: item.preloader,
			price: item.price,
			chance: item.chance,
			expensive: false
		}

		setItem(itemNumber, imgItem)
	}

	transform.value = props.horizontal
		? `translateX(${-(px + randomOffset)}px)`
		: `translateY(${-(px + randomOffset)}px)`

	const duration = !fast.value
		? 3000 + (props.startOffset || 0) * 300
		: 1000 + (props.startOffset || 0) * 100

	startHaptics()

	await sleep(duration)

	stopHaptics()

	animationColor.value = getItemColor(item, props.casePrice)

	pullBack.value = true
	transform.value = props.horizontal
		? `translateX(${-px}px)`
		: `translateY(${-px}px)`

	await sleep(200)

	if (pullbackCallback) {
		pullbackCallback()
	}
	if (item.price > props.casePrice) {
		pullBackAnimate.value = true
	}
	pullBackRipple.value = true

	rouletteItems.value[itemNumber].large = true

	if (item.chance < 1) {
		animateExpensive.value = true
		rouletteItems.value[itemNumber].reveal = true

		playHapticSequence(['error', 'error', 'error'])
	} else if (item.price > props.casePrice) {
		WebApp.HapticFeedback.impactOccurred('heavy')
	}

	if (item.price > props.casePrice) {
		stickerRefs.value[itemNumber].play()

		if (!fast.value) {
			await sleep(3000)
		} else {
			await sleep(1000)
		}
	} else {
		WebApp.HapticFeedback.impactOccurred('light')
		await sleep(500)
	}

	rouletteItems.value[itemNumber].large = false

	await sleep(500)

	stopAnimationFrame()
}

defineExpose({
	roll
})
</script>
